import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Form, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { getFailureMessageRxOp } from '@geekbears/gb-ngrx-stack-base';
import { ModalComponent, NotificationsService } from '@geekbears/ngx-gb-ui-kit';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { isNil } from 'lodash';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';

import {
  ClientsActions,
  getPendingRequestsByMemberFailureAction,
  getPendingRequestsByMemberSuccessAction,
  removeAndAssignSuccessAction,
  removeClientFailureAction,
  removeClientSuccessAction,
  removeMemberFailureAction,
  removeMemberSuccessAction,
  removeAndAssignLoadingSelector,
  removeClientLoadingSelector,
  removeMemberLoadingSelector,
} from '@modules/company-manager/clients/application';
import { Client, Member } from '@modules/company-manager/clients/domain';

import { ApprovesActions } from '@modules/regular-user/approves/application';
import {
  approversByCompanyLoadingSelector,
  approversByCompanySuccessSelector,
} from '@modules/regular-user/approves/application';
import { AppUser } from '@modules/company-manager/users/domain';

@Component({
  selector: 'app-client-modals',
  templateUrl: './client-modals.modal.html',
  styleUrls: ['./client-modals.modal.scss'],
})
export class ClientModals extends ModalComponent implements OnInit, OnDestroy {
  /**
   * @destroy$ is used to unsubscribe from all subscriptions
   */
  destroy$: Subject<void> = new Subject();
  modalType: string = '';
  client!: Client;
  member!: Member;
  pendingRequests: number = 0;
  removeClientLoading$: Observable<boolean | any>;
  approvers$!: Observable<AppUser[]>;
  approversLoading$!: Observable<boolean | any>;
  removeMemberLoading$: Observable<boolean | any>;
  _newApprover!: string;
  newApproverForm!: FormGroup;
  removeAndAssignLoading$: Observable<boolean | any>;
  constructor(
    @Inject(MAT_DIALOG_DATA) data: any,
    private dialogRef: MatDialogRef<ClientModals>,
    private store$: Store,
    private actions$: Actions,
    private notificationsService: NotificationsService,
    private router: Router,
    private fb: FormBuilder
  ) {
    super();
    if (data) {
      this.modalType = data.modalType;
      if (data.client) this.client = data.client;
      if (data.member) {
        this.member = data.member;
      }
    }
    this.removeAndAssignLoading$ = this.store$.select(
      removeAndAssignLoadingSelector
    );
    this.removeMemberLoading$ = this.store$.select(removeMemberLoadingSelector);
    this.approvers$ = this.store$
      .select(approversByCompanySuccessSelector)
      .pipe(
        filter((approvers) => {
          return !isNil(approvers);
        }),
        map((approvers) => {
          return approvers!.filter(
            (approver: any) => approver.id != this.member._id
          );
        })
      );
    this.approversLoading$ = this.store$.select(
      approversByCompanyLoadingSelector
    );
    this.removeClientLoading$ = this.store$.select(removeClientLoadingSelector);
    this.newApproverForm = this.fb.group({
      newApprover: ['', Validators.required],
    });
  }
  ngOnInit(): void {
    this.onFailureActions();
    this.onDeleteClientSuccess();
    this.onRemoveMemberSuccess();
    this.getRequestPendingByClient();
    this.onPendingRequestByMemberSuccess();
    this.onRequestsReassigned();
  }

  getApprovers() {
    this.store$.dispatch(
      ApprovesActions.getApproversByCompany({
        companyId: this.member._companyId,
      })
    );
  }

  onDeleteClientSuccess() {
    this.actions$
      .pipe(
        ofType(removeClientSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action) => {
          if (action) {
            this.modalType = 'deleteClientSuccess';

            return action.response.data;
          }
        })
      )
      .subscribe();
  }
  onPendingRequestByMemberSuccess() {
    this.actions$
      .pipe(
        ofType(getPendingRequestsByMemberSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action: any) => {
          this.pendingRequests = action.response.data;
          this.getApprovers();
          if (action.response.data) {
          }
        })
      )
      .subscribe();
  }

  navigateToClientList() {
    this.router.navigateByUrl('dashboard/clients');
    this.closeModal();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  onRemoveMemberSuccess() {
    this.actions$
      .pipe(
        ofType(removeMemberSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action) => {
          if (action) {
            this.modalType = 'removeMemberSuccess';
            return action.response.data;
          }
        })
      )
      .subscribe();
  }
  closeModal() {
    this.dialogRef.close();
  }
  removeMember() {
    if (this.member.id)
      this.store$.dispatch(
        ClientsActions.removeMember({
          memberId: this.member.id,
        })
      );
  }

  dispatchRemoveClient() {
    this.store$.dispatch(
      ClientsActions.removeClient({
        client: this.client._id,
      })
    );
  }

  onFailureActions() {
    this.actions$
      .pipe(
        ofType(
          removeMemberFailureAction,
          removeClientFailureAction,
          getPendingRequestsByMemberFailureAction
        ),
        map((action) => {
          return action.failure;
        }),
        getFailureMessageRxOp(),
        tap((errorMessage) => {
          if (errorMessage) {
            this.notificationsService.error(errorMessage, {
              duration: 5000,
              icon: 'alert-outline',
            });
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  getRequestPendingByClient() {
    if (this.member)
      this.store$.dispatch(
        ClientsActions.getPendingRequestsByMember({
          member: this.member.id,
        })
      );
  }

  removeAndAssign() {
    if (this.newApproverForm.valid)
      this.store$.dispatch(
        ClientsActions.removeAndAssign({
          data: {
            _newApproverId: this.newApproverForm.get('newApprover')?.value,
            _oldApproverId: this.member._id,
          },
        })
      );
  }
  onRequestsReassigned() {
    this.actions$
      .pipe(
        ofType(removeAndAssignSuccessAction),
        filter((action) => {
          return !isNil(action.response);
        }),
        map((action: any) => {
          if (action) {
            this.store$.dispatch(
              ClientsActions.removeMember({
                memberId: this.member._id,
              })
            );
          }
        })
      )
      .subscribe();
  }
}
